import React, { useState } from 'react';
import { AlertTriangle, Trash2, X, Loader } from 'lucide-react';

const DeleteCompanyModal = ({ isOpen, onClose, companyName, companyId, onConfirmDelete }) => {
  const [confirmationText, setConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1); // 1: Warning, 2: Confirmation

  const expectedText = 'DELETE';
  const isConfirmationValid = confirmationText.toUpperCase() === expectedText;

  const handleDelete = async () => {
    if (!isConfirmationValid) {
      setError('Please type DELETE to confirm');
      return;
    }

    setIsDeleting(true);
    setError('');

    try {
      const result = await onConfirmDelete(companyId);
      
      if (result.success) {
        // Success - parent component will handle redirect
        onClose();
      } else {
        setError(result.error || 'Failed to delete company');
        setIsDeleting(false);
      }
    } catch (err) {
      setError(err.message || 'An error occurred during deletion');
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (!isDeleting) {
      setConfirmationText('');
      setError('');
      setStep(1);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Delete Company</h2>
          </div>
          {!isDeleting && (
            <button
              onClick={handleClose}
              className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors touch-manipulation"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 1 ? (
            // Step 1: Warning
            <>
              <div className="mb-6">
                <p className="text-gray-700 mb-4">
                  You are about to delete <span className="font-bold text-gray-900">"{companyName}"</span>
                </p>
                
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                  <p className="font-semibold text-red-800 mb-2">⚠️ This action cannot be undone!</p>
                  <p className="text-sm text-red-700 mb-2">Deleting this company will permanently remove:</p>
                  <ul className="text-sm text-red-700 list-disc list-inside space-y-1 ml-2">
                    <li>All company data and settings</li>
                    <li>All sales and purchase records</li>
                    <li>All parties (customers/vendors)</li>
                    <li>All items and inventory</li>
                    <li>All payments and receipts</li>
                    <li>All employees and team members</li>
                    <li>All reports and documents</li>
                    <li>Subscription and billing history</li>
                  </ul>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setStep(2)}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-5 h-5" />
                  Continue
                </button>
              </div>
            </>
          ) : (
            // Step 2: Confirmation
            <>
              <div className="mb-6">
                <p className="text-gray-700 mb-4">
                  To confirm deletion, please type <span className="font-bold text-red-600">DELETE</span> below:
                </p>
                
                <input
                  type="text"
                  value={confirmationText}
                  onChange={(e) => {
                    setConfirmationText(e.target.value);
                    setError('');
                  }}
                  placeholder="Type DELETE"
                  disabled={isDeleting}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100 disabled:cursor-not-allowed font-semibold text-center text-lg"
                  autoFocus
                />

                {error && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    💡 <span className="font-semibold">Note:</span> You will be redirected to the dashboard after deletion.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Back
                </button>
                <button
                  onClick={handleDelete}
                  disabled={!isConfirmationValid || isDeleting}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-5 h-5" />
                      Delete Permanently
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteCompanyModal;


































